import type { ApolloError } from 'apollo-client';
import type { FetchResult } from 'apollo-link';

import type { CheckoutSession } from '@/generated/graphql';
import { apolloClient } from '@/lib/apollo';
import { appStore } from '@/store';
import { getToken } from '@/lib';
import { logger } from '@/lib/logger';
import { OB_ERROR_KEYS } from '@/lib/utils/openBanking/handleOpenBankingReturn';
import { approvePayment } from './api';
import { CLEAR_REAL_TIME_BALANCE_CONTINGENCY_MUTATION } from './mutations';

interface ClearRTBMutationData {
  clearRealTimeBalanceContingency?: CheckoutSession | null;
}

interface DisallowedInstrumentData {
  id?: string | null;
  reason: string;
}

const showOpenBankingError = (contentKey: string) => {
  const { ui: uiActions } = appStore.getActions();
  uiActions.setPageWarningMessage(contentKey);
  uiActions.toggleIsFormSubmitting(false);
  globalThis.scrollTo(0, 0); // eslint-disable-line no-undef
};

export const onAfterClearRTBSuccess =
  (bankId: string) =>
  (response: FetchResult<ClearRTBMutationData>): Promise<void> | void => {
    const { disallowedInstruments = [], fundingOptions = [] } =
      response?.data?.clearRealTimeBalanceContingency || {};

    const { api: apiState } = appStore.getState();
    const { api: apiActions } = appStore.getActions();

    const disallowedBank = (
      disallowedInstruments as DisallowedInstrumentData[]
    )?.find((di: DisallowedInstrumentData) => di?.id === bankId);

    if (disallowedBank) {
      logger.error('open_banking_bank_disallowed', {
        bankId,
        reason: disallowedBank.reason,
      });
      logger.fpti('GUEST_OB_RESOLVE_CONTINGENCY_BANK_DISALLOWED', {
        ob_bank_id: bankId,
        ob_disallowed_reason: disallowedBank.reason,
        state_name: 'open_banking',
        transition_name: 'resolve_contingency_bank_disallowed',
      });
      showOpenBankingError(OB_ERROR_KEYS.TERMINAL);
      return;
    }

    const fundingOption = fundingOptions?.find(
      fo => fo?.fundingInstrument?.id === bankId,
    );

    if (!fundingOption) {
      logger.error('open_banking_bank_not_in_response', { bankId });
      logger.fpti('GUEST_OB_RESOLVE_CONTINGENCY_BANK_REJECTED', {
        ob_bank_id: bankId,
        state_name: 'open_banking',
        transition_name: 'resolve_contingency_bank_rejected',
      });
      showOpenBankingError(OB_ERROR_KEYS.TERMINAL);
      return;
    }

    const hasUnresolvedConsentContingency = fundingOption.allPlans?.some(
      (plan: any) =>
        plan?.fundingContingencies?.some(
          (contingency: any) =>
            contingency?.contingencyType === 'NEED_CONSENT_FOR_BANK_ACCOUNT' &&
            contingency?.encryptedId === bankId,
        ),
    );

    if (hasUnresolvedConsentContingency) {
      logger.error('open_banking_consent_contingency_unresolved', { bankId });
      logger.fpti('GUEST_OB_RESOLVE_CONTINGENCY_CONSENT_UNRESOLVED', {
        ob_bank_id: bankId,
        state_name: 'open_banking',
        transition_name: 'resolve_contingency_consent_unresolved',
      });
      showOpenBankingError(OB_ERROR_KEYS.AUTH);
      return;
    }

    const plan = fundingOption.allPlans?.[0];
    const fundingSource = apiState.isBillingWithoutPurchase
      ? fundingOption
      : plan?.fundingSources?.[0];

    const instrumentId = fundingSource?.fundingInstrument?.id || '';
    if (!instrumentId) {
      logger.error('open_banking_missing_instrument_id', { bankId });
      logger.fpti('GUEST_OB_RESOLVE_CONTINGENCY_NO_INSTRUMENT', {
        ob_bank_id: bankId,
        state_name: 'open_banking',
        transition_name: 'resolve_contingency_no_instrument',
      });
      showOpenBankingError(OB_ERROR_KEYS.TERMINAL);
      return;
    }

    apiActions.setInstrumentId(instrumentId);
    apiActions.setFundingSource(fundingSource);

    logger.fpti('GUEST_OB_RESOLVE_CONTINGENCY_SUCCESS', {
      ob_bank_id: bankId,
      state_name: 'open_banking',
      transition_name: 'resolve_contingency_success',
    });

    logger.metric('GUEST_SIGNUP.OPEN_BANKING.CONTINGENCY_RESOLVED', {
      dimensions: { status: 'success' },
    });

    return approvePayment() as Promise<void>;
  };

export const onAfterClearRTBError =
  (bankId: string) =>
  (err: ApolloError): void => {
    const gqlErrors = err.graphQLErrors || [];
    const firstError = gqlErrors[0] || err;
    const errorMessage = firstError?.message || err.message || '';

    logger.error('open_banking_resolve_contingency_failed', {
      bankId,
      errorMessage,
    });

    logger.fpti('GUEST_OB_RESOLVE_CONTINGENCY_FAILED', {
      int_error_code: errorMessage,
      ob_bank_id: bankId,
      state_name: 'open_banking',
      transition_name: 'resolve_contingency_failed',
    });

    logger.metric('GUEST_SIGNUP.OPEN_BANKING.CONTINGENCY_RESOLVED', {
      dimensions: { error: errorMessage, status: 'failure' },
    });

    if (errorMessage === 'BANK_LOGIN_CREDENTIALS_INVALID') {
      showOpenBankingError(OB_ERROR_KEYS.AUTH);
    } else {
      showOpenBankingError(OB_ERROR_KEYS.INTERNAL);
    }
  };

/**
 * Called after a successful return from MoneyNodeWeb.  Clears the real-time
 * balance contingency via GraphQL, stores the instrument ID from the
 * response, and proceeds to approve the payment.
 */
export const resolveOpenBankingContingency = (
  bankId: string,
): Promise<void> => {
  const token = getToken();
  const { ui: uiActions } = appStore.getActions();

  logger.fpti('GUEST_OB_RESOLVE_CONTINGENCY_START', {
    ob_bank_id: bankId,
    state_name: 'open_banking',
    transition_name: 'resolve_contingency_start',
  });
  logger.info('open_banking_resolve_contingency_start', { bankId });

  uiActions.toggleIsFormSubmitting(true);

  return apolloClient
    .mutate({
      context: { skipFallback: true },
      mutation: CLEAR_REAL_TIME_BALANCE_CONTINGENCY_MUTATION,
      variables: { bankId, token },
    })
    .then(onAfterClearRTBSuccess(bankId))
    .catch(onAfterClearRTBError(bankId));
};
